import React from 'react';
import PropTypes from 'prop-types';

import { UploadedPhoto, PrintSize, DeliveryOption } from '../../prop-types';
import Photo from '../photos-grid/photo';
import Utils from '../../common/size-helpers';
import PhotoFilename from '../../common/photo-filename';
import SimpleThumbnail from '../photos-grid/photo/simple-thumbnail';
import fireTrackingEvent from '../../common/events/fire-tracking-event';
import { WebUI, builderDispatch } from '../../../_utils/ui';
import { UpdatePhotoEvents } from '../../common/events/event-declarations';
import { QuantityInput } from './quantity-input';
import {
  sizesWithRootImage,
  sizesWithPrice,
} from '../../common/totalizing-mixin';
import ResizeOptionsModal from '../resize-options-modal';
import Button from '../../../_common/button';

const SizeOptionsMode = {
  NONE: undefined,
  RESIZE: 'resize',
  ADDSIZE: 'add_size',
};

class PhotoSelectionView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      sizeOptionsMode: SizeOptionsMode.NONE,
    };
  }

  showResizeOptionsModal = () => {
    this.setState({ sizeOptionsMode: SizeOptionsMode.RESIZE });
  };

  showAddSizeOptionsModal = () => {
    this.setState({ sizeOptionsMode: SizeOptionsMode.ADDSIZE });
  };

  handleCloseSizeOptionsModal = () => {
    this.setState({ sizeOptionsMode: SizeOptionsMode.NONE });
  };

  update = (value, key = 'quantity') => {
    const { selectedPhoto } = this.props;

    builderDispatch.dispatch({
      type: UpdatePhotoEvents.UPDATE,
      photo: {
        ...selectedPhoto,
        [key]: value,
      },
      hard: true,
    });
  };

  handleEdit = (photo, onAdvancedEdit) => {
    const {
      size: { key },
      image_id: imageId,
    } = photo;
    fireTrackingEvent(WebUI.PHOTO_EVENT_ADVANCED_EDIT);
    onAdvancedEdit(key, imageId);
  };

  handleQuantityUpdate = value => this.update(value, 'quantity');

  handleRemove = (photo, nextPhoto) => {
    const {
      size: { key },
      image_id: imageId,
    } = photo;

    builderDispatch.dispatch({
      type: UpdatePhotoEvents.REMOVE,
      sizeKey: key,
      imageId,
      selectedPhotoId: nextPhoto?.image_id,
    });
  };

  handleRemoveByRootId = rootImageId => {
    builderDispatch.dispatch({
      type: UpdatePhotoEvents.SHOW_REMOVE_CONFIRMATION,
      imageId: rootImageId,
      useRoot: true,
    });
  };

  handleSizeChange = (targetSizeKey, photo) => {
    builderDispatch.dispatch({
      type: UpdatePhotoEvents.RESIZE,
      image_id: photo.image_id,
      size_key: photo.size.key,
      targetSizeKey,
    });
  };

  addSize(targetSizeKey, photo) {
    builderDispatch.dispatch({
      type: UpdatePhotoEvents.ADD_TO_SIZE,
      root_image_id: photo.root_image_id,
      targetSizeKey,
      quantity: 1,
    });
  }

  render() {
    const {
      selectedPhoto,
      setSelectedPhotoId,
      availableSizes,
      sizes,
      onAdvancedEditPhoto,
      orderedPhotos,
      recommendedPrintSizes,
      selectedPhotoAssociatedPhotos,
      deliveryOption,
    } = this.props;
    const { sizeOptionsMode } = this.state;

    const currentPhotoIndex = orderedPhotos.findIndex(photo =>
      photo.photos.map(p => p.image_id).includes(selectedPhoto.image_id)
    );
    const prevPhoto = orderedPhotos[currentPhotoIndex - 1];
    const nextPhoto = orderedPhotos[currentPhotoIndex + 1];
    const currentSizeIndex = selectedPhotoAssociatedPhotos.findIndex(
      p => p.image_id === selectedPhoto.image_id
    );
    const prevSizePhoto = selectedPhotoAssociatedPhotos[currentSizeIndex - 1];
    const nextSizePhoto = selectedPhotoAssociatedPhotos[currentSizeIndex + 1];
    const isLastPhoto = selectedPhotoAssociatedPhotos.length <= 1;
    const recommendedPrintSize = recommendedPrintSizes[selectedPhoto.image_id];

    return (
      <div className="prints-builder__photo-view d-flex flex-column">
        <div className="prints-builder__photo-view-header d-flex justify-content-between">
          <button
            className="btn btn-link btn-underline ml-2"
            onClick={() => setSelectedPhotoId(Utils.ALL_PHOTOS_KEY)}
            aria-label="Go back to the gallery of photos in your order">
            <i className="fas fa-chevron-left mr-1" />
            Back
          </button>

          <QuantityInput
            key={`${selectedPhoto.image_id}-${selectedPhoto.quantity}`}
            className="mt-3 mr-2"
            value={selectedPhoto.quantity}
            onQuantityChange={this.handleQuantityUpdate}
          />
        </div>

        <div className="prints-builder__photo-view__photo flex-fill">
          <div className="flex-grow-1">
            <div className="d-flex justify-content-center py-2">
              <Button
                className="btn-lg"
                icon="fa-chevron-down ml-2"
                aria-label="Select another print size for your photo"
                text={`Size: ${selectedPhoto.size.title}`}
                onClick={this.showResizeOptionsModal}
              />
            </div>

            <div className="ml-4 mb-2">
              <PhotoFilename photo={selectedPhoto} />
            </div>

            {Photo(
              { ...selectedPhoto, key: `${selectedPhoto.image_id}` },
              false,
              sizesWithRootImage(sizes, selectedPhoto.image_id),
              availableSizes,
              onAdvancedEditPhoto,
              recommendedPrintSize,
              SimpleThumbnail,
              false,
              {
                showLowQualityWarning: true,
                lowQualityInformationPosition: 'bottom',
                lowQualityInformationCSSClasses: 'mobile',
                isWallet: false,
              }
            )}

            <div className="d-flex justify-content-between mt-2 px-4">
              <button
                className="btn btn-link btn-underline"
                aria-label={`Remove ${selectedPhoto.filename} ${selectedPhoto.size.title}`}
                onClick={() =>
                  isLastPhoto
                    ? this.handleRemoveByRootId(selectedPhoto.root_image_id)
                    : this.handleRemove(
                        selectedPhoto,
                        prevSizePhoto || nextSizePhoto
                      )
                }>
                <i className="fa fa-trash mr-2" />
                Remove
              </button>

              <button
                className="btn btn-link btn-underline"
                aria-label={`Edit ${selectedPhoto.filename} ${selectedPhoto.size.title}`}
                onClick={() =>
                  this.handleEdit(selectedPhoto, onAdvancedEditPhoto)
                }>
                <i className="fa fa-pencil-alt mr-2" />
                Edit
              </button>
            </div>
          </div>
        </div>

        <div className="row d-flex mt-auto mb-2 px-3">
          <div className="col p-0 text-center">
            {prevSizePhoto && (
              <>
                <span className="w-100 text-center">Prev Size</span>
                <button
                  className="btn btn-pill"
                  onClick={() => setSelectedPhotoId(prevSizePhoto.image_id)}
                  aria-label="Display the previous size of this photo">
                  <i className="fas fa-chevron-up mr-2" />
                  {prevSizePhoto.size.title}
                </button>
              </>
            )}
          </div>

          <div className="col p-0 text-center">
            <Button
              className="btn-pill add-size-btn"
              aria-label="Add a copy of this photo in a different size"
              text="Add Size"
              onClick={this.showAddSizeOptionsModal}
            />
          </div>

          <div className="col p-0 text-center">
            {nextSizePhoto && (
              <>
                <span className="w-100 text-center">Next Size</span>
                <button
                  className="btn btn-pill"
                  onClick={() => setSelectedPhotoId(nextSizePhoto.image_id)}
                  aria-label="Display the next size of this photo">
                  <i className="fas fa-chevron-down mr-2" />
                  {nextSizePhoto.size.title}
                </button>
              </>
            )}
          </div>
        </div>

        <footer className="prints-builder__footer prints-builder__photo-view-footer">
          <div className="prints-builder__footer-border d-flex justify-content-between align-items-center">
            {prevPhoto && (
              <button
                className="btn btn-pill mr-auto"
                onClick={() => setSelectedPhotoId(prevPhoto.photos[0].image_id)}
                aria-label="Display the previous photo in your order">
                <i className="fas fa-chevron-left mr-2" />
                Prev photo
              </button>
            )}

            {nextPhoto && (
              <button
                className="btn btn-pill ml-auto"
                onClick={() => setSelectedPhotoId(nextPhoto.photos[0].image_id)}
                aria-label="Display the next photo in your order">
                Next photo
                <i className="fas fa-chevron-right ml-2" />
              </button>
            )}
          </div>
        </footer>
        <ResizeOptionsModal
          title={'Resize'}
          availableSizes={sizesWithPrice(availableSizes, sizes, deliveryOption)}
          sizesWithPrints={sizesWithRootImage(
            sizes,
            selectedPhoto.root_image_id
          )}
          onClick={targetSizeKey => {
            this.handleSizeChange(targetSizeKey, selectedPhoto);
            this.handleCloseSizeOptionsModal();
          }}
          showResizeOptionsModal={sizeOptionsMode === SizeOptionsMode.RESIZE}
          deliveryOption={deliveryOption}
          handleResizeOptionsModal={this.handleCloseSizeOptionsModal}
          recommendedPrintSize={recommendedPrintSize}
          photoDimensions={selectedPhoto.raw_dimensions}
          showLowQualityWarning
          hideTooltip
        />
        <ResizeOptionsModal
          title={'Add Size'}
          availableSizes={sizesWithPrice(availableSizes, sizes, deliveryOption)}
          sizesWithPrints={sizesWithRootImage(
            sizes,
            selectedPhoto.root_image_id
          )}
          onClick={targetSizeKey => {
            this.addSize(targetSizeKey, selectedPhoto);
            this.handleCloseSizeOptionsModal();
          }}
          showResizeOptionsModal={sizeOptionsMode === SizeOptionsMode.ADDSIZE}
          deliveryOption={deliveryOption}
          handleResizeOptionsModal={this.handleCloseSizeOptionsModal}
          recommendedPrintSize={recommendedPrintSize}
          photoDimensions={selectedPhoto.raw_dimensions}
          showLowQualityWarning
          hideTooltip
        />
      </div>
    );
  }
}

PhotoSelectionView.propTypes = {
  setSelectedPhotoId: PropTypes.func.isRequired,
  selectedPhoto: UploadedPhoto.isRequired,
  orderedPhotos: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedPhotoAssociatedPhotos: PropTypes.arrayOf(UploadedPhoto).isRequired,
  recommendedPrintSizes: PropTypes.objectOf(PrintSize),
  sizes: PropTypes.arrayOf(PrintSize),
  availableSizes: PropTypes.arrayOf(PrintSize),
  deliveryOption: DeliveryOption.isRequired,
  onAdvancedEditPhoto: PropTypes.func.isRequired,
};

export default PhotoSelectionView;
